import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tab,
  Tabs,
  Box,
  Grid,
  Avatar,
  Typography,
  CircularProgress,
  useTheme,
  alpha,
  Fab
} from '@mui/material';
import {
  EmojiEmotions,
  Pets,
  Person,
  SmartToy,
  Castle,
  Add
} from '@mui/icons-material';

import AvatarCreator from './AvatarCreator';

const AVATAR_CATEGORIES = {
  emoji: {
    name: 'Emojis',
    icon: EmojiEmotions,
    color: '#FF9800',
    path: '/uploads/avatars/emoji'
  },
  animals: {
    name: 'Animales',
    icon: Pets,
    color: '#4CAF50',
    path: '/uploads/avatars/animals'
  },
  personas: {
    name: 'Personas',
    icon: Person,
    color: '#2196F3',
    path: '/uploads/avatars/personas'
  },
  robots: {
    name: 'Robots',
    icon: SmartToy,
    color: '#9C27B0',
    path: '/uploads/avatars/robots'
  },
  fantasia: {
    name: 'Fantasía',
    icon: Castle,
    color: '#FF5722',
    path: '/uploads/avatars/fantasia'
  }
};

// Listas estáticas de avatares por categoría
const AVATAR_SEEDS = {
  emoji: [
    'alex', 'jordan', 'taylor', 'casey', 'jamie', 'sam', 'alexis', 'riley',
    'charlie', 'harper', 'morgan', 'river', 'sky', 'phoenix', 'nova', 'quinn',
    'indigo', 'blake', 'hayden', 'oakley', 'marley', 'finley', 'dakota', 'kai',
    'emerson', 'noa', 'parker', 'jules', 'rivera', 'spencer'
  ],
  animals: [
    'bear', 'lion', 'tiger', 'elephant', 'giraffe', 'zebra', 'panda', 'koala',
    'fox', 'wolf', 'rabbit', 'cat', 'dog', 'owl', 'eagle', 'dolphin',
    'whale', 'shark', 'turtle', 'frog', 'penguin', 'flamingo', 'peacock', 'swan', 'deer'
  ],
  personas: [
    'maria', 'juan', 'ana', 'carlos', 'sofia', 'luis', 'carmen', 'pedro',
    'isabel', 'antonio', 'elena', 'jose', 'lucia', 'manuel', 'patricia', 'david',
    'laura', 'francisco', 'marta', 'miguel', 'cristina', 'rafael', 'pilar', 'alberto', 'rosa'
  ],
  robots: [
    'r2d2', 'c3po', 'wall-e', 'eva', 'optimus', 'bumblebee', 'megatron', 'starscream',
    'ironhide', 'jazz', 'ratchet', 'prowl', 'bluestreak', 'sideswipe', 'sunstreaker',
    'wheeljack', 'mirage', 'trailbreaker', 'hound', 'cliffjumper'
  ],
  fantasia: [
    'wizard', 'elf', 'dwarf', 'orc', 'dragon', 'fairy', 'knight', 'archer',
    'mage', 'warrior', 'paladin', 'rogue', 'bard', 'druid', 'ranger', 'monk',
    'sorcerer', 'warlock', 'cleric', 'barbarian'
  ]
};

const AvatarSelector = ({
  open,
  onClose,
  onSelect
}) => {
  const theme = useTheme();
  const [selectedCategory, setSelectedCategory] = useState('emoji');
  const [creatorOpen, setCreatorOpen] = useState(false);

  // Pre-generar todos los avatares usando useMemo para evitar re-cálculos
  const allAvatars = useMemo(() => {
    const result = {};
    Object.keys(AVATAR_CATEGORIES).forEach(categoryKey => {
      const category = AVATAR_CATEGORIES[categoryKey];
      const seeds = AVATAR_SEEDS[categoryKey] || [];

      result[categoryKey] = seeds.map(seed => ({
        id: `${categoryKey}-${seed}`,
        name: seed.charAt(0).toUpperCase() + seed.slice(1),
        url: `${category.path}/${seed}.png`,
        category: categoryKey
      }));
    });
    return result;
  }, []); // Empty dependency array - this never changes

  // Reset to first category when modal opens
  useEffect(() => {
    if (open) {
      setSelectedCategory('emoji');
    }
  }, [open]);

  // Manejar cambio de categoría - función simple sin useCallback
  const handleCategoryChange = (event, newValue) => {
    if (newValue && newValue !== selectedCategory) {
      setSelectedCategory(newValue);
    }
  };

  // Manejar selección de avatar - función simple
  const handleAvatarSelect = (avatar) => {
    if (onSelect && avatar?.url) {
      onSelect(avatar.url);
    }
    if (onClose) {
      onClose();
    }
  };

  // Manejar creador de avatares
  const handleOpenCreator = () => {
    setCreatorOpen(true);
  };

  const handleCloseCreator = () => {
    setCreatorOpen(false);
  };

  const handleSaveCustomAvatar = (avatarUrl, file) => {
    if (onSelect) {
      onSelect(avatarUrl);
    }
    setCreatorOpen(false);
    if (onClose) {
      onClose();
    }
  };

  const currentAvatars = allAvatars[selectedCategory] || [];
  const currentCategory = AVATAR_CATEGORIES[selectedCategory];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: '80vh'
        }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
          Seleccionar Avatar
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Elige un avatar de las categorías disponibles
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={selectedCategory}
            onChange={handleCategoryChange}
            variant="fullWidth"
            indicatorColor="primary"
            sx={{
              '& .MuiTabs-indicator': {
                backgroundColor: currentCategory?.color || theme.palette.primary.main,
                height: 3
              }
            }}
          >
            {Object.keys(AVATAR_CATEGORIES).map((categoryKey) => {
              const category = AVATAR_CATEGORIES[categoryKey];
              const IconComponent = category.icon;

              return (
                <Tab
                  key={categoryKey}
                  value={categoryKey}
                  icon={<IconComponent />}
                  label={category.name}
                  sx={{
                    color: theme.palette.text.secondary,
                    '&.Mui-selected': {
                      color: category.color,
                    },
                    minHeight: 64,
                    textTransform: 'none',
                    fontWeight: 500
                  }}
                />
              );
            })}
          </Tabs>
        </Box>

        <Box sx={{ p: 3, maxHeight: 400, overflowY: 'auto' }}>
          {currentAvatars.length === 0 ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
              <Typography variant="body2" color="text.secondary">
                No hay avatares disponibles en esta categoría
              </Typography>
            </Box>
          ) : (
            <Grid container spacing={2}>
              {currentAvatars.map((avatar) => (
                <Grid item xs={3} sm={2} md={2} key={avatar.id}>
                  <Box
                    onClick={() => handleAvatarSelect(avatar)}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      cursor: 'pointer',
                      p: 1,
                      borderRadius: 1,
                      border: '2px solid transparent',
                      '&:hover': {
                        backgroundColor: alpha(currentCategory.color, 0.1),
                        borderColor: alpha(currentCategory.color, 0.3),
                        transform: 'scale(1.02)'
                      }
                    }}
                  >
                    <Avatar
                      src={avatar.url}
                      alt={avatar.name}
                      sx={{
                        width: 56,
                        height: 56,
                        mb: 0.5,
                        border: `2px solid ${alpha(currentCategory.color, 0.2)}`
                      }}
                    />
                    <Typography
                      variant="caption"
                      align="center"
                      sx={{
                        fontSize: '0.7rem',
                        lineHeight: 1.2,
                        maxWidth: '100%',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {avatar.name}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, justifyContent: 'space-between' }}>
        <Button
          variant="outlined"
          startIcon={<Add />}
          onClick={handleOpenCreator}
          sx={{
            borderColor: '#FF6B35',
            color: '#FF6B35',
            '&:hover': {
              borderColor: '#E55A2B',
              backgroundColor: alpha('#FF6B35', 0.1)
            }
          }}
        >
          Crear Avatar Personalizado
        </Button>

        <Button
          onClick={onClose}
          sx={{
            color: theme.palette.text.secondary,
            '&:hover': {
              backgroundColor: alpha(theme.palette.text.secondary, 0.1)
            }
          }}
        >
          Cancelar
        </Button>
      </DialogActions>

      {/* Creador de avatares personalizado */}
      <AvatarCreator
        open={creatorOpen}
        onClose={handleCloseCreator}
        onSave={handleSaveCustomAvatar}
      />
    </Dialog>
  );
};

export default AvatarSelector;